import type {
  RiskPictureItem,
  RiskPictureKind,
  RiskPictureLevel,
  RiskPictureModel,
} from "./risk-picture-model";

const RANK: Record<RiskPictureLevel, number> = { yellow: 1, orange: 2, red: 3 };

/**
 * Most severe level among `items`, e.g. one day's warnings after the map filters.
 * Issued warnings and kansverwachtingen both count. Undefined when no item carries a level.
 */
export const highestRiskPictureItemLevel = (
  items: readonly RiskPictureItem[],
): RiskPictureLevel | undefined => {
  let highest: RiskPictureLevel | undefined;
  for (const item of items) {
    if (item.level && (!highest || RANK[item.level] > RANK[highest])) {
      highest = item.level;
    }
  }
  return highest;
};

/**
 * Level and kind for a day-strip bar. Same level as `highestRiskPictureItemLevel`; on a tie
 * an issued warning wins, so the bar only gets the chance stripes when a kansverwachting
 * is the sole reason for that code.
 */
export const highestRiskPictureItemCode = (
  items: readonly RiskPictureItem[],
): { level: RiskPictureLevel; kind: RiskPictureKind } | undefined => {
  const level = highestRiskPictureItemLevel(items);
  if (!level) return undefined;
  const issued = items.some((item) => item.level === level && item.kind === "issued");
  return { level, kind: issued ? "issued" : "chance" };
};

/**
 * Most severe level across every day in the model. Issued warnings and
 * kansverwachtingen both count unless `kind` limits the scan.
 * Undefined when no matching item carries a level.
 */
export const highestRiskPictureLevel = (
  model: RiskPictureModel,
  kind?: RiskPictureKind,
): RiskPictureLevel | undefined => {
  const items = model.days.flatMap((day) => day.items);
  return highestRiskPictureItemLevel(
    kind ? items.filter((item) => item.kind === kind) : items,
  );
};
